import { QuoteControlType } from '@src/components/sidepanel/CompositionSettings/types';
import {
  getDimensionSize,
  resolveElementFromPath,
} from '@src/utility/compositionSettings';
import { getImageNaturalDimensions } from '@src/utility/naturalDimensions';

import { LogManager } from './Logger';

import type { ControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';
import type { QuoteCardProps } from '@src/services/composition/CompositionElementService';

/**
 * Quote card-specific helper functions to extract values from QuoteCardProps
 */

/**
 * Internal helper: Adjusts the background image element's dimensions to maintain aspect ratio
 * while ensuring they are at least as large as the original dimensions.
 */
export async function adjustBackgroundImageAspectRatio(
  source: Record<string, unknown>,
  control: ControlConfiguration,
  imageUrl: string,
): Promise<{ elementId: string; width: string; height: string }[] | undefined> {
  const logger = LogManager.getLogger(
    'quoteCardsCompositionSettings:adjustBackgroundImageAspectRatio',
  );
  const result: { elementId: string; width: string; height: string }[] = [];

  try {
    for (const path of control.elementPaths) {
      // Get source dimensions
      const sourceWidth =
        typeof source.width === 'number' ? source.width : 1920;
      const sourceHeight =
        typeof source.height === 'number' ? source.height : 1080;

      // Find the target element
      const targetElement = resolveElementFromPath(source, path);
      if (!targetElement) {
        logger.warn('Could not find target element for background image');
        continue;
      }

      const currentWidth = targetElement.width || '100%';
      const currentHeight = targetElement.height || '100%';

      if (
        typeof currentWidth !== 'string' ||
        typeof currentHeight !== 'string'
      ) {
        logger.warn('Element width/height not in expected string format');
        continue;
      }

      // Parse percentage values
      const widthMatch = currentWidth.match(/^(\d+(?:\.\d+)?)\s*%$/);
      const heightMatch = currentHeight.match(/^(\d+(?:\.\d+)?)\s*%$/);

      if (!widthMatch || !heightMatch) {
        logger.warn('Could not parse width/height percentages');
        continue;
      }

      const originalWidthPercent = parseFloat(widthMatch[1]);
      const originalHeightPercent = parseFloat(heightMatch[1]);

      // Convert percentages to pixels based on source dimensions
      const originalWidthPx = (originalWidthPercent / 100) * sourceWidth;
      const originalHeightPx = (originalHeightPercent / 100) * sourceHeight;

      // Get image natural dimensions
      const dimensions = await getImageNaturalDimensions(imageUrl);
      if (!dimensions) {
        logger.warn('Could not fetch image dimensions for:', imageUrl);
        continue;
      }

      const { width: imageWidth, height: imageHeight } = dimensions;
      const imageAspectRatio = imageWidth / imageHeight;

      // Calculate new dimensions in pixels that:
      // 1. Maintain the image's aspect ratio
      // 2. Are at least as large as the original dimensions
      let newWidthPx = originalWidthPx;
      let newHeightPx = originalHeightPx;

      // Calculate what the height should be based on the width and aspect ratio
      const heightBasedOnWidth = originalWidthPx / imageAspectRatio;

      // Calculate what the width should be based on the height and aspect ratio
      const widthBasedOnHeight = originalHeightPx * imageAspectRatio;

      // Choose the dimension that ensures both width and height are at least as large as originals
      if (heightBasedOnWidth >= originalHeightPx) {
        // Width stays the same, adjust height
        newHeightPx = heightBasedOnWidth;
      } else if (widthBasedOnHeight >= originalWidthPx) {
        // Height stays the same, adjust width
        newWidthPx = widthBasedOnHeight;
      } else {
        // Need to increase both dimensions
        // Choose the option that increases the total area the least
        const areaOption1 =
          originalWidthPx * (originalWidthPx / imageAspectRatio);
        const areaOption2 =
          originalHeightPx * imageAspectRatio * originalHeightPx;

        if (areaOption1 <= areaOption2) {
          newWidthPx = originalWidthPx;
          newHeightPx = originalWidthPx / imageAspectRatio;
        } else {
          newWidthPx = originalHeightPx * imageAspectRatio;
          newHeightPx = originalHeightPx;
        }
      }

      // Convert back to percentages based on source dimensions
      const newWidthPercent = (newWidthPx / sourceWidth) * 100;
      const newHeightPercent = (newHeightPx / sourceHeight) * 100;

      result.push({
        elementId: targetElement.id || '',
        width: `${Math.round(newWidthPercent)}%`,
        height: `${Math.round(newHeightPercent)}%`,
      });
    }
    return result;
  } catch (error) {
    logger.error('Error adjusting background image aspect ratio:', error);
  }
}

export function getDefaultQuoteTextValue(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  if (control.type !== 'text') return undefined;
  if (control.default?.quote !== QuoteControlType.TEXT) return undefined;
  return typeof quoteCardProps?.text === 'string' &&
    quoteCardProps?.text?.length > 0
    ? quoteCardProps?.text
    : '';
}

export function getDefaultQuoteNameValue(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  if (control.type !== 'text') return undefined;
  if (control.default?.quote !== QuoteControlType.NAME) return undefined;
  return typeof quoteCardProps?.name === 'string' &&
    quoteCardProps?.name?.length > 0
    ? quoteCardProps?.name
    : '';
}

export function getDefaultQuoteOrgValue(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  if (control.type !== 'text') return undefined;
  if (control.default?.quote !== QuoteControlType.ORG) return undefined;
  return typeof quoteCardProps?.org === 'string' &&
    quoteCardProps?.org?.length > 0
    ? quoteCardProps?.org
    : '';
}

export function getDefaultQuoteBackgroundValue(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  if (control.type !== 'image') return undefined;
  if (control.default?.quote !== QuoteControlType.BACKGROUND) return undefined;
  return typeof quoteCardProps?.background === 'string' &&
    quoteCardProps?.background?.length > 0
    ? quoteCardProps?.background
    : '';
}

export function getDefaultQuoteHeadshotValue(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  if (control.type !== 'image' && control.type !== 'headshot') {
    return undefined;
  }
  if (control.default?.quote !== QuoteControlType.HEADSHOT) {
    return undefined;
  }
  return typeof quoteCardProps?.headshot === 'string' &&
    quoteCardProps?.headshot?.length > 0
    ? quoteCardProps?.headshot
    : undefined;
}

/**
 * Extract quote card value overrides from QuoteCardProps.
 * This function converts quote-specific props into generic property overrides.
 */
export function getQuoteCardValueOverrides(
  control: ControlConfiguration,
  quoteCardProps?: QuoteCardProps,
): unknown {
  const quoteValue = getDefaultQuoteTextValue(control, quoteCardProps);
  if (quoteValue !== undefined) return quoteValue;
  const nameValue = getDefaultQuoteNameValue(control, quoteCardProps);
  if (nameValue !== undefined) return nameValue;
  const orgValue = getDefaultQuoteOrgValue(control, quoteCardProps);
  if (orgValue !== undefined) return orgValue;
  const backgroundValue = getDefaultQuoteBackgroundValue(
    control,
    quoteCardProps,
  );
  if (backgroundValue !== undefined) return backgroundValue;
  const headshotValue = getDefaultQuoteHeadshotValue(control, quoteCardProps);
  if (headshotValue !== undefined) return headshotValue;
  return undefined;
}

/**
 * Apply quote card-specific source modifications (dimensions).
 * This is called after the generic source is created.
 */
export async function applyQuoteCardSourceModifications(
  source: Record<string, unknown>,
  quoteCardProps?: QuoteCardProps,
): Promise<void> {
  if (quoteCardProps?.dimension) {
    const dimensions = getDimensionSize(quoteCardProps.dimension);
    source.width = dimensions.width;
    source.height = dimensions.height;
  }
}
